import { useState } from "react";
import { useSession } from "@/lib/auth-client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Eye, RotateCcw, Save } from "lucide-react";
import {
  useUserSettings,
  useUpdateUserSettings,
  usePreviewTemplate,
  UserSettings,
} from "@/hooks/use-user-settings";
import { PreviewModal } from "@/components/molecules/PreviewModal";

const TEMPLATE_VARIABLES = [
  { name: "{contactName}", description: "Contact's name" },
  { name: "{suburb}", description: "Property suburb" },
  { name: "{reportDate}", description: "Report date" },
  { name: "{salesCount}", description: "Number of sales" },
  { name: "{listingsCount}", description: "Number of listings" },
  { name: "{propertyTypes}", description: "Property types filter" },
];

interface TemplateSettingsFormProps {
  settings: UserSettings | null | undefined;
  defaultName: string;
  defaultEmail: string;
}

function TemplateSettingsForm({
  settings,
  defaultName,
  defaultEmail,
}: TemplateSettingsFormProps) {
  const updateSettings = useUpdateUserSettings();
  const previewTemplate = usePreviewTemplate();

  // Initialize form state from settings or defaults
  const [emailSubject, setEmailSubject] = useState(
    settings?.emailSubjectTemplate || "",
  );
  const [introMessage, setIntroMessage] = useState(
    settings?.emailIntroMessage || "",
  );
  const [outroMessage, setOutroMessage] = useState(
    settings?.emailOutroMessage || "",
  );
  const [agentName, setAgentName] = useState(
    settings?.agentSignatureName || defaultName,
  );
  const [agentEmail, setAgentEmail] = useState(
    settings?.agentSignatureEmail || defaultEmail,
  );
  const [agentPhone, setAgentPhone] = useState(
    settings?.agentSignaturePhone || "",
  );
  const [agentCompany, setAgentCompany] = useState(
    settings?.agentSignatureCompany || "",
  );

  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buildPayload = () => ({
    emailSubjectTemplate: emailSubject || null,
    emailIntroMessage: introMessage || null,
    emailOutroMessage: outroMessage || null,
    agentSignatureName: agentName || null,
    agentSignatureEmail: agentEmail || null,
    agentSignaturePhone: agentPhone || null,
    agentSignatureCompany: agentCompany || null,
  });

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    try {
      await updateSettings.mutateAsync(buildPayload());
      setSuccess("Email template saved successfully!");
      setTimeout(() => setSuccess(null), 5000);
    } catch {
      setError("Failed to save template");
    }
  };

  const handlePreview = async () => {
    setError(null);

    try {
      const result = await previewTemplate.mutateAsync(buildPayload());
      setPreviewHtml(result.html);
      setPreviewSubject(result.subject);
      setShowPreview(true);
    } catch {
      setError("Failed to generate preview");
    }
  };

  const handleReset = () => {
    if (
      window.confirm(
        "Are you sure you want to reset to default settings? This will clear all your customizations.",
      )
    ) {
      setEmailSubject("");
      setIntroMessage("");
      setOutroMessage("");
      setAgentName("");
      setAgentEmail("");
      setAgentPhone("");
      setAgentCompany("");
      setSuccess("Settings reset to defaults. Click Save to apply changes.");
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          Email Template
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize how your contact reports look. Use template variables to
          personalize each email.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 3 }}>
        {/* Template Variables Info */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Available Template Variables:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {TEMPLATE_VARIABLES.map((variable) => (
              <Box
                key={variable.name}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Typography
                  component="code"
                  sx={{
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  }}
                >
                  {variable.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  - {variable.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>

        {/* Email Subject */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Email Subject Line"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Property Market Report - {suburb}"
            inputProps={{ maxLength: 200 }}
            helperText={`${emailSubject.length}/200 characters${!emailSubject ? ' • Default: "Property Market Report - {suburb}"' : ""}`}
          />
        </Box>

        {/* Opening Message */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Opening Message (Optional)"
            value={introMessage}
            onChange={(e) => setIntroMessage(e.target.value)}
            placeholder="Hi {contactName},&#10;&#10;Here is your latest property market report for {suburb}..."
            multiline
            rows={5}
            inputProps={{ maxLength: 2000 }}
            helperText={`${introMessage.length}/2000 characters • This message appears before the sales and listings tables`}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Agent Signature Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Agent Contact Signature
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This information appears in the email footer so contacts know how to
            contact you.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Your Name"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="John Smith"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Your Email"
                type="email"
                value={agentEmail}
                onChange={(e) => setAgentEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Your Phone (Optional)"
                value={agentPhone}
                onChange={(e) => setAgentPhone(e.target.value)}
                placeholder="021 123 4567"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company Name (Optional)"
                value={agentCompany}
                onChange={(e) => setAgentCompany(e.target.value)}
                placeholder="Premier Realty NZ"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Closing Message */}
        <Box>
          <TextField
            fullWidth
            label="Closing Message (Optional)"
            value={outroMessage}
            onChange={(e) => setOutroMessage(e.target.value)}
            placeholder="If you have any questions about this report, please don't hesitate to contact me."
            multiline
            rows={4}
            inputProps={{ maxLength: 2000 }}
            helperText={`${outroMessage.length}/2000 characters • This message appears after the tables, before your signature${!outroMessage ? ' • Default: "This report was generated automatically. For questions, please contact your real estate agent."' : ""}`}
          />
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={
            previewTemplate.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <Eye size={18} />
            )
          }
          onClick={handlePreview}
          disabled={previewTemplate.isPending || updateSettings.isPending}
        >
          {previewTemplate.isPending ? "Generating..." : "Preview"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RotateCcw size={18} />}
          onClick={handleReset}
          disabled={updateSettings.isPending}
        >
          Reset to Defaults
        </Button>
        <Button
          variant="contained"
          startIcon={
            updateSettings.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Save size={18} />
            )
          }
          onClick={handleSave}
          disabled={updateSettings.isPending}
          sx={{ ml: "auto" }}
        >
          {updateSettings.isPending ? "Saving..." : "Save Template"}
        </Button>
      </Box>

      {/* Preview Modal */}
      <PreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        htmlContent={previewHtml}
        subject={previewSubject}
      />
    </Box>
  );
}

export function TemplateSettings() {
  const { data: session, isPending: isSessionPending } = useSession();
  const { data: settings, isLoading: isLoadingSettings } = useUserSettings();

  if (isSessionPending || isLoadingSettings) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Extract defaults from Better Auth session for initial form values
  const defaultName = session?.user?.name || "";
  const defaultEmail = session?.user?.email || "";

  // Use key to reset form when settings change (e.g., after save)
  return (
    <TemplateSettingsForm
      key={settings?.id || "new"}
      settings={settings}
      defaultName={defaultName}
      defaultEmail={defaultEmail}
    />
  );
}
